import { MUSIC_EVENTS, SLIDESHOW_AUDIO_FADE_OUT_MS } from 'Sp/Angular/Events/Client/Gallery';
import spMusicTemplate from '../../templates/components/sp-music.nghtml';

/**
 * @ngdoc component
 * @name sp.client.gallery.component:spMusic
 * @requires sp.client.common.service:SPMusicData
 * @requires $http
 * @requires $httpParamSerializer
 * @requires $element
 * @requires $scope
 * @requires spEventBus
 *
 * @description
 * Renders the music controls.
 *
 */
export default {
    template: spMusicTemplate,
    controller: [
        'SPMusicData',
        'SPClientRoute',
        '$http',
        '$httpParamSerializer',
        '$element',
        '$scope',
        '$timeout',
        'spEventBus',
        function spMusicController(
            SPMusicData,
            SPClientRoute,
            $http,
            $httpParamSerializer,
            $element,
            $scope,
            $timeout,
            spEventBus
        ) {
            const $ctrl = this;

            $ctrl.$onDestroy = $onDestroy;
            $ctrl.$onInit = $onInit;
            $ctrl.preventHideControls = false;
            // Tracks the gallery playlist's own audio element. Only ever true while that element
            // is actually sounding — a slideshow's song sounding is `!musicIsMuted` with
            // `isSlideshowAudioActive`, not this.
            $ctrl.musicIsPlaying = false;
            // The one shared "audio off" state for the whole gallery, governing whichever song is
            // currently sounding — the gallery playlist or a slideshow's own sound timeline. This
            // is what the control's icon reflects, and it survives navigation because <sp-music>
            // lives outside ng-view.
            $ctrl.musicIsMuted = true;
            $ctrl.volumeLevel = 0.5;
            // While true, a slideshow's own music is sounding — gallery music stays ducked and
            // won't even autoplay-start until it clears.
            $ctrl.isSlideshowAudioActive = false;
            // Set only when ducking a slideshow actually paused a *playing* playlist, so the
            // lightbox handoff can put it back exactly where it was and nothing else ever starts
            // music that wasn't already playing (a gallery with no playlist, or one the visitor
            // never started, stays silent).
            $ctrl.wasGalleryMusicPausedForSlideshow = false;
            // While true, the lightbox's own slideshow player is the page's audio and this control
            // governs nothing, so it hides rather than advertise control it doesn't have. Only the
            // lightbox sets this (see MUSIC_EVENTS.LIGHTBOX_SLIDESHOW_AUDIO) — the intro page's
            // cover slideshow *is* governed by this control, so it never hides there.
            $ctrl.isLightboxSlideshowAudioActive = false;
            // Whether setup got far enough to reveal the control, so re-showing it after the
            // lightbox finishes can never reveal one that was never ready in the first place.
            $ctrl.isMusicControlReady = false;
            // Whether the visitor has used the control at all. Their explicit choice always wins
            // over the studio's autoplay setting when audio is handed back to the gallery
            // playlist (see handleRouteChange).
            $ctrl.hasVisitorChosenAudioState = false;
            $ctrl.musicAutoPlay = false;
            // While true (the intro page), the gallery playlist must not play at all — see the
            // route config's `musicDeferred`. Only a slideshow cover's own song sounds there;
            // the playlist starts once the visitor reaches the gallery.
            $ctrl.isGalleryMusicDeferred = false;

            $ctrl.updateVolume = updateVolume;
            $ctrl.startOrPauseMusic = startOrPauseMusic;

            function $onInit() {
                // Read before setting music up, so the autoplay attempt in setupMusic can see it.
                $ctrl.isGalleryMusicDeferred = SPClientRoute.isMusicDeferredForRoute();

                if (SPMusicData.getAsync) {
                    SPMusicData.getAsync().then((data) => {
                        setupMusic(data);
                    });
                } else if (SPMusicData.get) {
                    setupMusic(SPMusicData.get());
                }

                $ctrl.offMusicMute = spEventBus.on(MUSIC_EVENTS.MUTE, muteForSlideshow);
                $ctrl.offMusicUnmute = spEventBus.on(MUSIC_EVENTS.UNMUTE, unmuteAfterSlideshow);
                $ctrl.offLightboxSlideshowAudio = spEventBus.on(
                    MUSIC_EVENTS.LIGHTBOX_SLIDESHOW_AUDIO,
                    handleLightboxSlideshowAudio
                );

                $scope.$on('$routeChangeSuccess', handleRouteChange);
            }

            function muteForSlideshow() {
                $ctrl.isSlideshowAudioActive = true;

                // Hand the "audio is on" state over rather than resetting it: the slideshow's own
                // song takes over sounding, at the same volume, so from the visitor's point of
                // view nothing about the control changed — only which song it governs, which is
                // why the icon-bound musicIsMuted is deliberately left alone here.
                if ($ctrl.currentAudio && $ctrl.musicIsPlaying) {
                    $ctrl.currentAudio.pause();
                    $ctrl.musicIsPlaying = false;
                    // Remember that *we* paused a playing playlist, so a lightbox slideshow
                    // finishing can hand sounding back (see resumeGalleryAfterSlideshow).
                    $ctrl.wasGalleryMusicPausedForSlideshow = true;
                }

                // Broadcast the shared volume and mute state right as a slideshow takes over
                // audio, so a just-activated slideshow syncs to both regardless of Angular/React
                // mount order — a slideshow mounting before this component has ever emitted on
                // its own would otherwise start at its own defaults rather than the shared ones.
                spEventBus.emit(MUSIC_EVENTS.VOLUME, $ctrl.volumeLevel);
                spEventBus.emit(MUSIC_EVENTS.MUTED, $ctrl.musicIsMuted);
            }

            // Only clears the "slideshow is using audio" bookkeeping — deliberately does not
            // resume gallery music. Only ever starting (never resuming) the other side keeps
            // gallery and slideshow music mutually exclusive without either fighting back. A
            // slideshow that was the page's *own* audio asks for the handoff explicitly instead,
            // via PLAY_GALLERY below.
            function unmuteAfterSlideshow() {
                $ctrl.isSlideshowAudioActive = false;
            }

            /**
             * Hides the control while the lightbox's slideshow player is the page's audio, and on
             * the way back out reveals it again and hands sounding back to the gallery playlist.
             * See MUSIC_EVENTS.LIGHTBOX_SLIDESHOW_AUDIO for why this is the lightbox's own event
             * rather than behavior on the shared MUTE/UNMUTE pair.
             *
             * The element is only hidden, never removed: <sp-music> owns the paused playlist's
             * audio element (and therefore its position), plus every music listener, so unmounting
             * it would break the resume below and the mute/volume plumbing with it.
             */
            function handleLightboxSlideshowAudio(isActive) {
                $ctrl.isLightboxSlideshowAudioActive = Boolean(isActive);

                if ($ctrl.isLightboxSlideshowAudioActive) {
                    $element.removeClass('ready');

                    return;
                }

                if ($ctrl.isMusicControlReady) {
                    $element.addClass('ready');
                }

                resumeGalleryAfterSlideshow();
            }

            /**
             * Reveals the control, unless the lightbox player is currently the page's audio — in
             * which case it stays hidden and `handleLightboxSlideshowAudio` reveals it once that
             * player stops.
             */
            function markMusicControlReady() {
                $ctrl.isMusicControlReady = true;

                if (!$ctrl.isLightboxSlideshowAudioActive) {
                    $element.addClass('ready');
                }
            }

            /**
             * Picks the gallery playlist back up after a lightbox slideshow the visitor played
             * has stopped — the one case where one side resuming the other is wanted (see
             * MUSIC_EVENTS.LIGHTBOX_SLIDESHOW_AUDIO). Every guard below is a "do nothing" case
             * rather than an error: the playlist only ever resumes if ducking it is what paused it.
             */
            function resumeGalleryAfterSlideshow() {
                if (!$ctrl.wasGalleryMusicPausedForSlideshow) {
                    return;
                }

                $ctrl.wasGalleryMusicPausedForSlideshow = false;
                $ctrl.isSlideshowAudioActive = false;

                // The visitor muted while the slideshow was sounding: their choice governs
                // whichever song would sound next, so leave the playlist paused.
                if ($ctrl.musicIsMuted) {
                    return;
                }

                // Belt and braces — the playlist must never sound on a deferred route (the intro
                // page). The lightbox doesn't exist there, so this should be unreachable.
                if ($ctrl.isGalleryMusicDeferred) {
                    return;
                }

                // Resumes rather than restarts: muteForSlideshow's pause() left currentTime where
                // it was, so the visitor keeps their place in the song.
                playGalleryMusic();
            }

            // The gallery playlist belongs to the gallery, not the intro page: it starts the
            // moment the visitor crosses from a deferred route into the gallery, and only then.
            // Navigating around inside the gallery afterwards leaves the song alone (both sides
            // of this transition have to change for it to fire), so the playlist plays through
            // rather than restarting on every page.
            function handleRouteChange() {
                const wasDeferred = $ctrl.isGalleryMusicDeferred;

                $ctrl.isGalleryMusicDeferred = SPClientRoute.isMusicDeferredForRoute();

                if (!wasDeferred || $ctrl.isGalleryMusicDeferred || $ctrl.musicIsPlaying) {
                    return;
                }

                // Leaving the intro page tears down any slideshow cover along with it, so
                // whatever it was sounding is on its way out and the playlist is free to take
                // over — but a cover slideshow that *was* sounding fades out rather than cutting
                // (see CoverSlideshowBackground), so hold the playlist back until that fade has
                // finished instead of overlapping the two songs.
                const wasCoverSlideshowSounding = $ctrl.isSlideshowAudioActive;

                $ctrl.isSlideshowAudioActive = false;

                // A visitor who muted while a slideshow cover was sounding stays muted here.
                // Only when they never touched the control does the studio's autoplay setting
                // decide — deferring the playlist is what kept that autoplay from happening at
                // setup time.
                if (
                    $ctrl.musicIsMuted &&
                    ($ctrl.hasVisitorChosenAudioState || !$ctrl.musicAutoPlay)
                ) {
                    return;
                }

                $ctrl.musicIsMuted = false;

                if (!wasCoverSlideshowSounding) {
                    playGalleryMusicFromStart();

                    return;
                }

                $ctrl.galleryMusicFadeHandoff = $timeout(
                    playGalleryMusicFromStart,
                    SLIDESHOW_AUDIO_FADE_OUT_MS
                );
            }

            function $onDestroy() {
                $ctrl.musicIsPlaying = false;
                $ctrl.musicIsMuted = true;

                // A handoff still waiting on a cover slideshow's fade must not start the playlist
                // after this component is gone.
                if ($ctrl.galleryMusicFadeHandoff) {
                    $timeout.cancel($ctrl.galleryMusicFadeHandoff);
                    $ctrl.galleryMusicFadeHandoff = null;
                }

                if ($ctrl.currentAudio) {
                    $ctrl.currentAudio.pause();
                    $ctrl.currentAudio.removeEventListener('ended', $ctrl.loopMusic);
                    $ctrl.currentAudio = null;
                }

                $ctrl.offMusicMute?.();
                $ctrl.offMusicUnmute?.();
                $ctrl.offLightboxSlideshowAudio?.();
            }

            function updateVolume(level) {
                $ctrl.volumeLevel = level;

                if ($ctrl.currentAudio) {
                    $ctrl.currentAudio.volume = level;
                }

                spEventBus.emit(MUSIC_EVENTS.VOLUME, level);
            }

            // The control means "audio on/off for this gallery", not "play the gallery playlist":
            // whichever song is currently sounding — the playlist's or a slideshow's own — is the
            // one it governs.
            function startOrPauseMusic() {
                $ctrl.hasVisitorChosenAudioState = true;
                $ctrl.musicIsMuted = !$ctrl.musicIsMuted;

                // While a slideshow owns audio, the broadcast below *is* the whole action: the
                // slideshow silences its own song without stopping it, so it keeps running on the
                // slideshow's timeline and unmuting picks up where the slides are rather than
                // restarting. Starting the playlist here would layer a second song over it — and
                // on the intro page the playlist may not start at all, so the visitor's choice is
                // simply recorded and honored when they reach the gallery.
                if (!$ctrl.isSlideshowAudioActive && !$ctrl.isGalleryMusicDeferred) {
                    if ($ctrl.musicIsMuted) {
                        pauseGalleryMusic();
                    } else {
                        playGalleryMusic();
                    }
                }

                spEventBus.emit(MUSIC_EVENTS.MUTED, $ctrl.musicIsMuted);
            }

            function pauseGalleryMusic() {
                if (!$ctrl.currentAudio) {
                    return;
                }

                $ctrl.currentAudio.pause();
                $ctrl.musicIsPlaying = false;
                // The visitor paused this themselves — a slideshow finishing later must not
                // resurrect it (only ducking-for-a-slideshow earns a resume).
                $ctrl.wasGalleryMusicPausedForSlideshow = false;
            }

            // Entering the gallery always hears the playlist from the top, however much of it a
            // deferred route may have skipped. Pausing and resuming inside the gallery still
            // picks up where it left off — only arriving rewinds.
            function playGalleryMusicFromStart() {
                if (!$ctrl.currentAudio) {
                    return;
                }

                $ctrl.currentAudio.currentTime = 0;
                playGalleryMusic();
            }

            function playGalleryMusic() {
                if (!$ctrl.currentAudio) {
                    return;
                }

                $ctrl.currentAudio.volume = $ctrl.volumeLevel;
                $ctrl.currentAudio.play();
                $ctrl.musicIsPlaying = true;
                // However the playlist came to be sounding, there is no longer a ducked playlist
                // owed a resume.
                $ctrl.wasGalleryMusicPausedForSlideshow = false;
                // Mirror image of MUTE: tell any open slideshow to pause instead of playing
                // over the gallery music the visitor just started.
                spEventBus.emit(MUSIC_EVENTS.GALLERY_PLAYING);

                SPMusicData.setCurrentSongId($ctrl.currentSong.id);
            }

            function setupMusic(musicData) {
                // There is no song of our own to set up without a non-empty playlist. The previous
                // form of this guard compared `typeof playlistSongs` against `undefined` rather
                // than `'undefined'`, so that half never matched and only an explicit `false`
                // bailed — an absent or empty playlist fell through to `playlistSongs[0]` below
                // and threw.
                if (
                    !musicData.isMusicAllowed ||
                    !Array.isArray(musicData.playlistSongs) ||
                    musicData.playlistSongs.length === 0
                ) {
                    // On a route where the playlist is deferred, the control is here to govern a
                    // slideshow cover's own song rather than a playlist, so it still has a job to
                    // do and has to be visible (`sp-music:not(.ready)` is hidden). Its mute and
                    // volume handlers already work without any audio of our own — they broadcast
                    // MUTED/VOLUME for the slideshow to follow.
                    if ($ctrl.isGalleryMusicDeferred) {
                        markMusicControlReady();
                    }

                    return;
                }

                const autoPlay = musicData.musicAutoPlay;
                const playlistSongs = musicData.playlistSongs;

                $ctrl.musicAutoPlay = Boolean(autoPlay);

                const musicAuthToken = musicData.musicTsmAuthToken;
                let currentSongIndex = 0;

                $ctrl.addEventEnded = () => {
                    $ctrl.currentAudio.addEventListener('ended', $ctrl.loopMusic);
                };

                $ctrl.loopMusic = () => {
                    $ctrl.currentAudio.currentTime = 0;

                    $http.post(
                        `https://api.triplescoopmusic.com/v1/analytics/songs/${playlistSongs[currentSongIndex].id}/play`,
                        $httpParamSerializer({
                            authToken: musicAuthToken
                        }),
                        {
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded'
                            }
                        }
                    );

                    const nextSongIndex = currentSongIndex + 1;

                    if (!playlistSongs[nextSongIndex]) {
                        $ctrl.currentSong = playlistSongs[0];
                        SPMusicData.setCurrentSongId($ctrl.currentSong.id);

                        $ctrl.currentAudio = new Audio([$ctrl.currentSong.m]);
                        $ctrl.addEventEnded();
                        $ctrl.currentAudio.play();
                        currentSongIndex = 0;
                    } else {
                        $ctrl.currentSong = playlistSongs[nextSongIndex];
                        SPMusicData.setCurrentSongId($ctrl.currentSong.id);

                        $ctrl.currentAudio = new Audio([$ctrl.currentSong.m]);
                        $ctrl.addEventEnded();
                        $ctrl.currentAudio.play();
                        currentSongIndex++;
                    }
                };

                $ctrl.currentSong = playlistSongs[0];
                $ctrl.currentAudio = new Audio([$ctrl.currentSong.m]);
                $ctrl.addEventEnded();
                SPMusicData.setCurrentSongId($ctrl.currentSong.id);
                markMusicControlReady();

                if (
                    autoPlay &&
                    !$ctrl.isSlideshowAudioActive &&
                    // The intro page never autoplays the playlist; handleRouteChange picks this
                    // up when the visitor reaches the gallery.
                    !$ctrl.isGalleryMusicDeferred &&
                    $ctrl.currentAudio &&
                    $ctrl.currentAudio.paused
                ) {
                    $ctrl.currentAudio.volume = $ctrl.volumeLevel;

                    const promise = $ctrl.currentAudio.play();

                    if (promise !== undefined) {
                        promise
                            .then(() => {
                                $ctrl.currentAudio.play();
                                $ctrl.musicIsPlaying = true;
                                $ctrl.musicIsMuted = false;
                                // A slideshow mounting after this hands the sounding over rather
                                // than falling silent (see muteForSlideshow).
                                spEventBus.emit(MUSIC_EVENTS.MUTED, false);
                                spEventBus.emit(MUSIC_EVENTS.GALLERY_PLAYING);
                            })
                            .catch(() => {
                                $ctrl.musicIsPlaying = false;
                                $ctrl.musicIsMuted = true;
                            });
                    }
                }
            }
        }
    ]
};
